<template>
  <v-main>
    <v-container fluid>
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Support Tickets</h3>
              <v-spacer></v-spacer>
              <v-chip
                v-if="unreadCount > 0"
                color="red"
                text-color="white"
                class="mr-3"
              >
                <v-icon small left>mdi-bell</v-icon>
                {{ unreadCount }} Unread
              </v-chip>
              <!-- <v-btn color="primary" @click="createDialog = true">
                <v-icon left>mdi-plus</v-icon>
                Raise Ticket
              </v-btn> -->
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="4">
                  <v-text-field
                    solo
                    rounded
                    v-model="search"
                    label="Search tickets..."
                    color="#e60040"
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="search = null"
                  ></v-text-field>
                </v-col>
                <v-col md="3">
                  <v-select
                    solo
                    rounded
                    v-model="statusFilter"
                    :items="statusOptions"
                    label="Filter by Status"
                    clearable
                    @change="getTickets"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <v-progress-linear
                v-if="loading"
                indeterminate
                color="primary"
              ></v-progress-linear>
              <v-simple-table v-else fixed-header height="600" class="striped-table">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody v-if="filteredTickets.length > 0">
                    <tr v-for="ticket in filteredTickets" :key="ticket.id">
                      <td>
                        <div class="d-flex align-center">
                          <v-avatar color="primary" size="40" class="mr-3">
                            <span class="white--text">{{ getFirstLetter(ticket.title) }}</span>
                          </v-avatar>
                          <div>
                            <div class="font-weight-bold">{{ ticket.title }}</div>
                            <div class="text-caption grey--text">{{ ticket.ticket_code }}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <v-chip
                          :color="getStatusColor(ticket.status)"
                          text-color="white"
                          small
                        >
                          {{ formatStatus(ticket.status) }}
                        </v-chip>
                      </td>
                     
                      <td>{{ $options.filters.niceDate(ticket.created_at) || 'N/A' }}</td>
                      <td>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              small
                              @click="openChat(ticket)"
                              color="primary"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small>mdi-message</v-icon>
                            </v-btn>
                          </template>
                          <span>View Chat</span>
                        </v-tooltip>

                        <!-- Status Update Menu -->
                        <v-menu v-if="canMoveUp(ticket.status)" offset-y>
                          <template v-slot:activator="{ on, attrs }">
                            <v-tooltip bottom>
                              <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                                <v-btn
                                  icon
                                  small
                                  color="success"
                                  v-bind="{ ...attrs, ...tooltipAttrs }"
                                  v-on="{ ...on, ...tooltipOn }"
                                >
                                  <v-icon small>mdi-arrow-up-bold</v-icon>
                                </v-btn>
                              </template>
                              <span>Update Status</span>
                            </v-tooltip>
                          </template>
                          <v-list dense>
                            <v-list-item
                              v-for="status in getStatusOptions(ticket.status)"
                              :key="status.value"
                              @click="updateTicketStatus(ticket, status.value)"
                            >
                              <v-list-item-title>
                                <v-chip
                                  :color="getStatusColor(status.value)"
                                  text-color="white"
                                  x-small
                                >
                                  {{ status.text }}
                                </v-chip>
                              </v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>

                        <v-tooltip bottom v-if="canMoveDown(ticket.status)">
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              icon
                              small
                              color="warning"
                              @click="moveStatusDown(ticket)"
                              v-bind="attrs"
                              v-on="on"
                            >
                              <v-icon small>mdi-arrow-down-bold</v-icon>
                            </v-btn>
                          </template>
                          <span>{{ getNextStatusDown(ticket.status) }}</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="5" class="text-center py-5">
                        <v-icon size="64" color="grey">mdi-ticket-outline</v-icon>
                        <p class="grey--text mt-3">No tickets found</p>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Create Ticket Dialog -->
      <v-dialog v-model="createDialog" max-width="600px" persistent>
        <v-card rounded="xl">
          <v-card-title>
            <v-btn icon @click="createDialog = false">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <span class="ml-3 headline">Raise a ticket</span>
          </v-card-title>
          <v-card-subtitle class="pb-0">We're here to assist you 24/7. Choose a support option below.</v-card-subtitle>
          <v-divider></v-divider>
          <v-card-text class="pt-6">
            <v-form ref="ticketForm" v-model="formValid">
              <v-text-field
                v-model="newTicket.title"
                label="Ticket Title *"
                outlined
                dense
                :rules="[rules.required]"
                placeholder="e.g., Payment Issue - Transaction Failed"
              ></v-text-field>
              <v-textarea
                v-model="newTicket.message"
                label="Message *"
                outlined
                dense
                :rules="[rules.required]"
                rows="6"
                placeholder="Describe your issue in detail..."
              ></v-textarea>
            </v-form>
          </v-card-text>
          <v-card-actions class="px-6 pb-6">
            <v-btn
              color="primary"
              block
              x-large
              rounded
              @click="createTicket"
              :disabled="!formValid || creating"
              :loading="creating"
            >
              Submit Ticket
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Success Snackbar -->
      <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
        {{ snackbarMessage }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </v-main>
</template>

<script>
import axios from 'axios';

export default {
  name: 'TicketsList',
  data() {
    return {
      tickets: [],
      loading: false,
      creating: false,
      updating: false,
      search: null,
      statusFilter: null,
      formValid: false,
      unreadCount: 0,
      statusOptions: [
        { text: 'All', value: null },
        { text: 'Pending', value: 'pending' },
        { text: 'In Progress', value: 'in_progress' },
        { text: 'Resolved', value: 'resolved' }
      ],
      priorityOptions: [
        { text: 'Low', value: 'low' },
        { text: 'Medium', value: 'medium' },
        { text: 'High', value: 'high' }
      ],
      createDialog: false,
      newTicket: {
        title: '',
        message: ''
      },
      rules: {
        required: value => !!value || 'This field is required'
      },
      snackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success'
    };
  },
  computed: {
    isAdmin() {
      const user = this.$store.state.auth?.user;
      if (!user || !user.roles || !Array.isArray(user.roles)) return false;
      return user.roles.some(role => role.id === 1 || role.id === 2);
    },
    filteredTickets() {
      if (!this.search) return this.tickets;
      return this.tickets.filter(ticket => {
        const title = ticket.title || '';
        const ticketCode = ticket.ticket_code || '';
        return title.toLowerCase().includes(this.search.toLowerCase()) ||
               ticketCode.toLowerCase().includes(this.search.toLowerCase());
      });
    }
  },
  mounted() {
    this.getTickets();
    this.getUnreadCount();
    this.subscribeToUnreadCount();

    // Backup: Refresh unread count every 30 seconds (in case WebSocket disconnects)
    setInterval(() => {
      this.getUnreadCount();
    }, 30000);
  },
  beforeDestroy() {
    this.unsubscribeFromUnreadCount();
  },
  methods: {
    async getTickets() {
      this.loading = true;
      try {
        const params = {};
        if (this.statusFilter) {
          params.status = this.statusFilter;
        }
        const response = await axios.get('/tickets', { params });
        this.tickets = response.data.tickets || response.data.data || [];
      } catch (error) {
        console.error('Error fetching tickets:', error);
        this.showSnackbar('Failed to load tickets', 'error');
      } finally {
        this.loading = false;
      }
    },
    async getUnreadCount() {
      try {
        const response = await axios.get('/ticket-messages/unread-count');
        this.unreadCount = response.data.unread_count || 0;
      } catch (error) {
        console.error('Error fetching unread count:', error);
      }
    },
    async createTicket() {
      if (!this.$refs.ticketForm.validate()) {
        return;
      }

      this.creating = true;
      try {
        const user = this.$store.state.auth?.user;
        const payload = {
          user_id: user.id,
          title: this.newTicket.title,
          ticket_details: [
            {
              user_id: user.id,
              message: this.newTicket.message,
              is_admin: 0
            }
          ]
        };
        await axios.post('/tickets', payload);
        this.closeCreateDialog();
        this.getTickets();
        this.showSnackbar('Ticket created successfully', 'success');
      } catch (error) {
        console.error('Error creating ticket:', error);
        this.showSnackbar(error.response?.data?.message || 'Failed to create ticket', 'error');
      } finally {
        this.creating = false;
      }
    },
    closeCreateDialog() {
      this.createDialog = false;
      this.newTicket = { title: '', message: '' };
      if (this.$refs.ticketForm) {
        this.$refs.ticketForm.reset();
      }
    },
    // Status progression: pending -> in_progress -> resolved
    canMoveUp(status) {
      return status === 'pending' || status === 'in_progress';
    },
    canMoveDown() {
      return false; // No status can move down - resolved tickets are final
    },
    getStatusOptions(currentStatus) {
      // When clicking up arrow, show all available forward statuses
      if (currentStatus === 'pending') {
        return [
          { text: 'In Progress', value: 'in_progress' },
          { text: 'Resolved', value: 'resolved' }
        ];
      } else if (currentStatus === 'in_progress') {
        return [
          { text: 'Resolved', value: 'resolved' }
        ];
      }
      return [];
    },
    getNextStatusDown(currentStatus) {
      const statusMap = {
        'in_progress': 'Move to Pending',
        'resolved': 'Move to In Progress'
      };
      return statusMap[currentStatus] || '';
    },
    async moveStatusDown(ticket) {
      const statusMap = {
        'in_progress': 'pending',
        'resolved': 'in_progress'
      };
      const newStatus = statusMap[ticket.status];
      if (newStatus) {
        await this.updateTicketStatus(ticket, newStatus);
      }
    },
    async updateTicketStatus(ticket, newStatus) {
      this.updating = true;
      try {
        await axios.put(`/tickets/${ticket.id}`, {
          status: newStatus
        });
        this.getTickets();
        this.showSnackbar(`Status updated to ${this.formatStatus(newStatus)}`, 'success');
      } catch (error) {
        console.error('Error updating ticket status:', error);
        this.showSnackbar('Failed to update status', 'error');
      } finally {
        this.updating = false;
      }
    },
    openChat(ticket) {
      this.$router.push(`/tickets/${ticket.id}/chat`);
    },
    getStatusColor(status) {
      const colors = {
        pending: 'orange',
        in_progress: 'blue',
        resolved: 'green'
      };
      return colors[status] || 'grey';
    },
    getPriorityColor(priority) {
      const colors = {
        low: 'green',
        medium: 'orange',
        high: 'red'
      };
      return colors[priority] || 'grey';
    },
    formatStatus(status) {
      if (!status) return '';
      return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
    },
    formatDate(date) {
      if (!date) return 'N/A';
      const d = new Date(date);
      const today = new Date();
      const diffTime = Math.abs(today - d);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else if (diffDays === 1) {
        return 'Yesterday ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      } else {
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      }
    },
    truncate(text, length) {
      if (!text) return '';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },
    getFirstLetter(text) {
      if (!text || typeof text !== 'string') return '#';
      return text.charAt(0).toUpperCase();
    },
    showSnackbar(message, color) {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    subscribeToUnreadCount() {
      if (!this.$echo) {
        console.error('❌ Echo instance not available');
        return;
      }

      const user = this.$store.state.auth?.user;
      if (!user) {
        console.error('❌ User not authenticated');
        return;
      }

      console.log(`📡 Subscribing to user channel for unread count updates: user.${user.id}`);

      this.$echo.private(`user.${user.id}`)
        .listen('.unread.count.updated', (data) => {
          console.log('🔔 Unread count updated:', data.unread_count);
          this.unreadCount = data.unread_count;
        });

      console.log('✅ Subscribed to unread count updates');
    },
    unsubscribeFromUnreadCount() {
      if (this.$echo) {
        const user = this.$store.state.auth?.user;
        if (user) {
          console.log(`👋 Unsubscribing from user channel: user.${user.id}`);
          this.$echo.leave(`user.${user.id}`);
        }
      }
    }
  }
};
</script>

<style scoped>
.striped-table >>> tbody tr:nth-child(even) {
  background-color: #f5f5f5;
}
</style>
